import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BarChart3, Download } from 'lucide-react';
import { money } from '../lib/share';

const PERIODS = [['daily', 'Diario'], ['weekly', 'Semanal'], ['monthly', 'Mensual'], ['range', 'Entre fechas']];
const KPIS = [['sales_total', 'Ventas'], ['cash_sales_total', 'Contado'], ['credit_sales_total', 'Crédito'], ['down_payments', 'Iniciales'], ['collected', 'Cobrado'], ['installments_due', 'Cuotas del período'], ['overdue_total', 'Vencido total']];

const Kpis = ({ d }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
    {KPIS.map(([k, l]) => <div key={k} className="bg-[#F8F9FA] rounded-lg p-3"><p className="text-[11px] text-[#64748B]">{l}</p><p className={`font-heading font-black ${k === 'overdue_total' ? 'text-red-600' : 'text-[#022C22]'}`}>{money(d[k])}</p></div>)}
  </div>
);

export default function BranchReports() {
  const { user } = useAuth();
  const isOwner = ['master', 'superadmin'].includes(user?.role_id);
  const [f, setF] = useState({ period: 'daily', start: '', end: '', company_id: '' });
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const params = () => ({ period: f.period, start: f.start || undefined, end: f.end || undefined, company_id: f.company_id || undefined });
  const load = async () => { try { setData((await api.getBranchMovements(params())).data); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } };
  useEffect(() => { if (isOwner) api.getCompanies().then((r) => setCompanies(r.data)).catch(() => {}); }, [isOwner]);
  useEffect(() => { if (f.period !== 'range') load(); }, [f.period, f.company_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const excel = async () => {
    try {
      const r = await api.downloadBranchMovements(params());
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `movimientos_${f.period}.xlsx`; a.click();
    } catch { toast.error('SalesPRO: Error al exportar'); }
  };
  return (
    <div className="space-y-4" data-testid="branch-reports-page">
      <h1 className="font-heading text-3xl font-black text-[#022C22] flex items-center gap-2"><BarChart3 /> Reportes de movimientos por sucursal</h1>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-md border border-[#E2E8F0] overflow-hidden">
          {PERIODS.map(([k, l]) => <button key={k} data-testid={`period-${k}`} onClick={() => setF({ ...f, period: k })} className={`px-3 py-2 text-sm ${f.period === k ? 'bg-primary text-white' : 'bg-white text-[#64748B]'}`}>{l}</button>)}
        </div>
        {f.period === 'range' && <>
          <Input type="date" className="w-40" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="range-start" />
          <Input type="date" className="w-40" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="range-end" />
          <Button onClick={load} data-testid="range-apply">Consultar</Button>
        </>}
        {isOwner && (
          <select value={f.company_id} onChange={(e) => setF({ ...f, company_id: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" data-testid="report-branch-select">
            <option value="">Todas las sucursales (total y separado)</option>
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        )}
        <Button variant="outline" onClick={excel} data-testid="report-excel"><Download size={14} className="mr-1" /> Excel</Button>
      </div>
      {data && <>
        <p className="text-sm text-[#64748B]">Período {data.start} → {data.end}</p>
        {data.branches.length > 1 && <div data-testid="report-consolidated"><h3 className="font-bold text-[#022C22] mb-2">Consolidado</h3><Kpis d={data.consolidated} /></div>}
        {data.branches.map((b) => (
          <div key={b.company_id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 space-y-3" data-testid={`report-branch-${b.company_id}`}>
            <h3 className="font-heading font-bold text-lg text-[#022C22]">{b.company_name} <span className="text-xs text-[#94A3B8]">· {b.sales_count} venta(s)</span></h3>
            <Kpis d={b} />
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><p className="font-semibold text-[#022C22]">Cobros por método</p>{Object.entries(b.by_method).map(([k, v]) => <p key={k} className="flex justify-between"><span>{k}</span><b>{money(v)}</b></p>)}</div>
              <div><p className="font-semibold text-[#022C22]">Por vendedor</p>{b.by_seller.map((s) => <p key={s.seller} className="flex justify-between"><span>{s.seller} ({s.count})</span><b>{money(s.total)}</b></p>)}</div>
            </div>
            <div className="max-h-64 overflow-auto"><table className="w-full text-xs"><thead><tr className="text-left text-[#64748B] border-b"><th className="py-1">Fecha</th><th>Tipo</th><th>Doc</th><th>Cliente</th><th>Responsable</th><th className="text-right">Monto</th></tr></thead>
              <tbody>{b.movements.map((m, i) => <tr key={i} className="border-b border-[#F1F5F9]"><td className="py-1">{m.fecha}</td><td>{m.tipo}</td><td>{m.documento}</td><td>{m.cliente}</td><td>{m.responsable}</td><td className="text-right">{money(m.monto)}</td></tr>)}</tbody></table></div>
          </div>))}
      </>}
    </div>
  );
}
